import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';

interface DurationInputProps {
  value?: number;
  onChange: (value: number | null) => void;
  required?: boolean;
  disabled?: boolean;
  onRef?: (ref: { commit: () => void }) => void;
  /** Allows tenths of a second (for example 01:34:23.5). Default: false (whole seconds only, for example for setup times). */
  allowFractionalSeconds?: boolean;
}

export const DurationInput: React.FC<DurationInputProps> = ({ value, onChange, required = false, disabled = false, onRef, allowFractionalSeconds = false }) => {
  const [inputValue, setInputValue] = useState('');
  const hasChanged = useRef(false);
  const inputValueRef = useRef(inputValue);
  const onChangeRef = useRef(onChange);

  // Keep refs in sync
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const normalize = (seconds: number, allowFrac: boolean) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (allowFrac && s % 1 !== 0) {
      const sRounded = Math.round(s * 10) / 10;
      const intPart = Math.floor(sRounded);
      const fracPart = Math.round((sRounded - intPart) * 10);
      const sStr = `${String(intPart).padStart(2, '0')}.${fracPart}`;
      return `${h}:${String(m).padStart(2, '0')}:${sStr}`;
    }
    return `${h}:${String(m).padStart(2, '0')}:${String(Math.round(s)).padStart(2, '0')}`;
  };

  const parseInput = (text: string): number | null => {
    const trimmed = text.trim();

    if (trimmed === '') return required ? 0 : null;

    // HH:MM:SS.f or HH:MM:SS (with optional fractional part)
    const withFracMatch = trimmed.match(/^(\d+):(\d{1,2}):(\d{1,2})(?:\.(\d))?$/);
    if (withFracMatch) {
      const h = parseInt(withFracMatch[1], 10);
      const m = parseInt(withFracMatch[2], 10);
      const s = parseInt(withFracMatch[3], 10);
      const frac = withFracMatch[4] != null ? parseInt(withFracMatch[4], 10) / 10 : 0;
      if (m >= 0 && m <= 59 && s >= 0 && s <= 59 && frac >= 0 && frac < 1) {
        const total = h * 3600 + m * 60 + s + frac;
        return allowFractionalSeconds ? total : Math.round(total);
      }
    }

    // MM:SS.f or MM:SS
    const shortFracMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})(?:\.(\d))?$/);
    if (shortFracMatch) {
      const m = parseInt(shortFracMatch[1], 10);
      const s = parseInt(shortFracMatch[2], 10);
      const frac = shortFracMatch[3] != null ? parseInt(shortFracMatch[3], 10) / 10 : 0;
      if (m >= 0 && m <= 59 && s >= 0 && s <= 59 && frac >= 0 && frac < 1) {
        const total = m * 60 + s + frac;
        return allowFractionalSeconds ? total : Math.round(total);
      }
    }

    if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);

    const parts = trimmed.split(':').map(p => parseFloat(p));
    if (parts.some(isNaN)) return required ? 0 : null;

    if (parts.length === 2) {
      const total = parts[0] * 60 + parts[1];
      return allowFractionalSeconds ? total : Math.round(total);
    }
    if (parts.length === 3) {
      const total = parts[0] * 3600 + parts[1] * 60 + parts[2];
      return allowFractionalSeconds ? total : Math.round(total);
    }

    return required ? 0 : null;
  };

  // Stabilize commit function with useCallback to prevent infinite loops
  const commit = useCallback(() => {
    const currentInputValue = inputValueRef.current;
    const parsed = parseInput(currentInputValue);
    if (parsed !== null) {
      const normalized = normalize(parsed, allowFractionalSeconds);
      setInputValue(normalized);
      onChangeRef.current(parsed);
    } else {
      setInputValue('');
      onChangeRef.current(required ? 0 : null);
    }
    hasChanged.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parseInput is a local function; including it would run on every render
  }, [required, allowFractionalSeconds]);

  useEffect(() => {
    if (!hasChanged.current) {
      if (typeof value === 'number' && value >= 0) {
        const normalized = normalize(value, allowFractionalSeconds);
        setInputValue(normalized);
      } else {
        setInputValue('');
      }
    }
  }, [value, allowFractionalSeconds]);

  // Expose commit method via onRef (commit is now stable with useCallback)
  useEffect(() => {
    if (onRef) {
      onRef({ commit });
    }
  }, [onRef, commit]);

  const handleBlur = () => {
    commit();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    hasChanged.current = true;
  };

  return (
    <input
      className="e-input"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={allowFractionalSeconds ? 'hh:mm:ss.s' : 'hh:mm:ss'}
      disabled={disabled}
    />
  );
};

export const createDurationEditor = (field: string, required: boolean = false, allowFractionalSeconds: boolean = false) => {
  let latestValue: number | null = null;
  let container: HTMLElement;
  let root: ReturnType<typeof createRoot>;

  return {
    create: () => {
      container = document.createElement('div');
      return container;
    },
    write: (args: any) => {
      const value = args.rowData[field];
      root = createRoot(container);
      root.render(
        <DurationInput
          value={typeof value === 'number' ? value : undefined}
          required={required}
          allowFractionalSeconds={allowFractionalSeconds}
          onChange={(val) => {
            latestValue = val;
            args.rowData[field] = val; // sync immediately for grid updates
          }}
        />
      );
    },
    read: () => {
      return latestValue;
    },
    destroy: () => {
      if (root) root.unmount();
    }
  };
};
